import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getProductConfigOptions } from '@/modules/Settings/services/product'
import { ProductConfigOption } from '@/modules/Settings/types/product'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandedPricingSteps from './ExpandedPricingSteps'
import { getConfigOptionColumns } from './product-columns'

/** Expanded detail for a single product: config options with nested pricing steps */
const ExpandedProductDetail: React.FC<{
    productId: number
    readOnly?: boolean
}> = ({ productId, readOnly }) => {
    const { t } = useTranslation('settings')

    const { data, renderState, isFetching } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.PRODUCT_CONFIG_OPTIONS, productId],
            queryFn: () => getProductConfigOptions(productId),
        },
        { showLoading: true, showError: true }
    )

    const configColumns = useMemo(() => getConfigOptionColumns(t), [t])
    const configOptions = useMemo(
        () => (data || []).filter((o) => o.type !== 'information'),
        [data]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-3 py-2">
            <div>
                <div className="px-2 pb-1">
                    <span className="text-xs font-semibold text-muted-foreground">
                        {t('config-options')} ({configOptions.length})
                    </span>
                </div>
                <DataTableCustomized<ProductConfigOption>
                    columns={configColumns}
                    rows={configOptions}
                    rowKey={(r) => r.product_config_option_id}
                    emptyMessage={t('no-data')}
                    isFetching={isFetching}
                    expandable={{
                        expandedRowRender: (option) => (
                            <ExpandedPricingSteps
                                productId={productId}
                                option={option}
                                readOnly={readOnly}
                            />
                        ),
                        rowExpandable: () => true,
                    }}
                    bordered
                />
            </div>
        </div>
    )
}

export default ExpandedProductDetail
